import type { CanvasTarget } from '../types';
import { id as canonicalId } from '../../../src/artifacts/models/common.mjs';

export type ArtifactCanvasTarget = {
  kind: 'artifact';
  id: string;
  revisionId: string;
  versionId: string;
  versionNumber: number;
  purpose: 'viewer' | 'editor';
  jobId?: string;
  selectionSetId?: string;
  rowId?: string;
  tab?: string;
  full?: boolean;
  activityMessageId?: string;
  activityTab?: 'steps' | 'sources';
  step?: string;
  runId?: never;
};
export type DraftCanvasTarget = Omit<
  ArtifactCanvasTarget,
  'kind' | 'revisionId' | 'rowId' | 'versionId' | 'versionNumber' | 'purpose'
> & {
  kind: 'artifact-draft';
  revisionId?: never;
  rowId?: never;
  versionId?: never;
  versionNumber?: never;
  purpose?: never;
};
export function isArtifactCanvas(
  target: CanvasTarget | null,
): target is ArtifactCanvasTarget | DraftCanvasTarget {
  return target?.kind === 'artifact' || target?.kind === 'artifact-draft';
}
function validId(value: unknown): value is string {
  return canonicalId.safeParse(value).success;
}
/** Original Library identity only. URL/row purpose is a request hint; the
 * version reader independently checks current authority before disclosure. */
export function savedArtifactCanvasTarget(input: {
  artifactId: string;
  revisionId: string;
  versionId: string;
  versionNumber: number;
  purpose: 'viewer' | 'editor';
}): ArtifactCanvasTarget | null {
  if (
    !input ||
    ![input.artifactId, input.revisionId, input.versionId].every(validId) ||
    !Number.isSafeInteger(input.versionNumber) ||
    input.versionNumber < 1 ||
    (input.purpose !== 'viewer' && input.purpose !== 'editor')
  )
    return null;
  return {
    kind: 'artifact',
    id: input.artifactId,
    revisionId: input.revisionId,
    versionId: input.versionId,
    versionNumber: input.versionNumber,
    purpose: input.purpose,
  };
}
export function parseArtifactCanvas(
  params: URLSearchParams,
): ArtifactCanvasTarget | DraftCanvasTarget | null {
  const kind = params.get('canvas');
  const id = params.get('id');
  if ((kind !== 'artifact' && kind !== 'artifact-draft') || !validId(id)) return null;
  const fields = ['id', 'revisionId', 'versionId', 'jobId', 'selectionSetId', 'rowId'];
  if (
    fields.some(
      (key) => params.getAll(key).length > 1 || (params.has(key) && !validId(params.get(key))),
    )
  )
    return null;
  if (['canvas', 'versionNumber', 'purpose'].some((key) => params.getAll(key).length > 1))
    return null;
  const common = {
    id,
    ...(params.get('jobId') ? { jobId: params.get('jobId')! } : {}),
    ...(params.get('selectionSetId') ? { selectionSetId: params.get('selectionSetId')! } : {}),
  };
  if (kind === 'artifact-draft')
    return ['revisionId', 'rowId', 'versionId', 'versionNumber', 'purpose'].some((key) =>
      params.has(key),
    )
      ? null
      : { ...common, kind };
  const ordinal = params.get('versionNumber');
  if (!ordinal || !/^[1-9][0-9]*$/.test(ordinal)) return null;
  const saved = savedArtifactCanvasTarget({
    artifactId: id,
    revisionId: params.get('revisionId')!,
    versionId: params.get('versionId')!,
    versionNumber: Number(ordinal),
    purpose: params.get('purpose') as 'viewer' | 'editor',
  });
  return saved
    ? { ...common, ...saved, ...(params.get('rowId') ? { rowId: params.get('rowId')! } : {}) }
    : null;
}
export function artifactCanvasQuery(target: CanvasTarget): string {
  if (!isArtifactCanvas(target)) return '';
  const fields = [
    'revisionId',
    'versionId',
    'versionNumber',
    'purpose',
    'jobId',
    'selectionSetId',
    'rowId',
  ] as const;
  return fields
    .map((key) => (target[key] ? `&${key}=${encodeURIComponent(String(target[key]))}` : ''))
    .join('');
}
/** Sharing links keep the selected original, requesting read-only admission. */
export function artifactShareUrl(href: string, target: CanvasTarget): string {
  if (target.kind !== 'artifact') return href;
  const url = new URL(href);
  const at = url.hash.indexOf('?');
  if (at < 0) return href;
  const params = new URLSearchParams(url.hash.slice(at + 1));
  if (
    params.get('canvas') !== 'artifact' ||
    params.get('id') !== target.id ||
    params.get('versionId') !== target.versionId
  )
    return href;
  params.set('purpose', 'viewer');
  url.hash = url.hash.slice(0, at + 1) + params.toString();
  return url.href;
}

/** Scope identity for asynchronous loads. URL hints never confer authority. */
export function artifactCanvasScope(target: CanvasTarget | null): readonly (string | null)[] {
  return isArtifactCanvas(target)
    ? [
        target.kind,
        target.id,
        target.revisionId ?? null,
        target.versionId ?? null,
        target.versionNumber === undefined ? null : String(target.versionNumber),
        target.purpose ?? null,
        target.jobId ?? null,
        target.selectionSetId ?? null,
      ]
    : [];
}
